/**
 * Julgamento gerencial — transforma métricas OEE/IEL em linguagem executiva.
 * Responde: "Tenho um problema?" (não apenas números).
 */

import type { IelModel } from './ielService';
import type { TplOeeMetrics } from './lstOeeMetrics';
import { adherenceBand } from './tplAdherenceAudit';
import {
  formatExceptionCount,
  severityFromAdherenceBand,
  severityFromPct,
  TPL_SEVERITY_LABEL,
} from './tplSeverityStyles';

export interface TplExecutiveJudgmentCard {
  key: 'disciplina' | 'conformidade' | 'escalonamento' | 'resultado';
  metricLabel: string;
  value: string;
  detail: string | null;
  severity: TplSeverity;
  severityLabel: string;
}

export function buildExecutiveJudgments(
  oee: TplOeeMetrics,
  iel?: Pick<IelModel, 'resultadoAvailable' | 'resultado' | 'classification'>,
  escalonamentoPct?: number,
): TplExecutiveJudgmentCard[] {
  const dBand = adherenceBand(oee.disciplinaPct);
  const cBand = adherenceBand(oee.conformidadePct);

  const disciplina: TplExecutiveJudgmentCard = {
    key: 'disciplina',
    metricLabel: 'Comparecimento às rotinas',
    value: `${oee.disciplinaPct}%`,
    detail:
      formatExceptionCount(oee.counts.naoRealizado, 'não realizada', 'não realizadas') ??
      (oee.counts.planned > 0 && oee.disciplinaPct >= 90
        ? `${oee.counts.executadas}/${oee.counts.planned} executadas`
        : null),
    severity: severityFromAdherenceBand(dBand),
    severityLabel: TPL_SEVERITY_LABEL[severityFromAdherenceBand(dBand)],
  };

  const conformidade: TplExecutiveJudgmentCard = {
    key: 'conformidade',
    metricLabel: 'Execução correta',
    value: `${oee.conformidadePct}%`,
    detail:
      formatExceptionCount(oee.counts.desvio, 'desvio', 'desvios') ??
      (oee.counts.conforme > 0 && oee.conformidadePct >= 90
        ? `${oee.counts.conforme} conforme(s)`
        : null),
    severity: severityFromAdherenceBand(cBand),
    severityLabel: TPL_SEVERITY_LABEL[severityFromAdherenceBand(cBand)],
  };

  const resultado: TplExecutiveJudgmentCard = iel?.resultadoAvailable
    ? {
        key: 'resultado',
        metricLabel: 'Resultado operacional',
        value: `${iel.resultado}%`,
        detail: iel.resultado < 70 ? 'Abaixo da meta operacional' : null,
        severity: severityFromPct(iel.resultado),
        severityLabel: TPL_SEVERITY_LABEL[severityFromPct(iel.resultado)],
      }
    : {
        key: 'resultado',
        metricLabel: 'Resultado operacional',
        value: 'Pendente',
        detail: 'Rotinas sem indicador vinculado',
        severity: 'informativo',
        severityLabel: 'Pendente',
      };

  const escalonamento: TplExecutiveJudgmentCard =
    escalonamentoPct != null
      ? {
          key: 'escalonamento',
          metricLabel: 'Suporte da liderança',
          value: `${escalonamentoPct}%`,
          detail: escalonamentoPct < 85 ? 'Escalonamentos ou reuniões de camada em atraso' : null,
          severity: severityFromPct(escalonamentoPct),
          severityLabel: TPL_SEVERITY_LABEL[severityFromPct(escalonamentoPct)],
        }
      : {
          key: 'escalonamento',
          metricLabel: 'Suporte da liderança',
          value: 'N/A',
          detail: null,
          severity: 'informativo',
          severityLabel: 'N/A',
        };

  return [disciplina, conformidade, escalonamento, resultado];
}
